import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Plus, Search, Users } from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import { UserDataContext } from "../context/User";
import Breadcrumb from "../component/Breadcrumb";
import BannerImg from "../image/comunity.png";
import Banner from "../component/Banner";

const categories = [
  { key: "all", label: "All" },
  { key: "introductions", label: "Introductions" },
  { key: "technique", label: "Technique" },
  { key: "inspiration", label: "Inspiration" },
  { key: "showcase", label: "Showcase" },
  { key: "general", label: "General" },
];

function authorName(author) {
  if (!author) return "LWBL Member";
  const fullName = [author.firstName, author.lastName].filter(Boolean).join(" ");
  return author.preferredName || fullName || author.memberId || "LWBL Member";
}

function timeAgo(value) {
  if (!value) return "Recently";
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(value).toLocaleDateString();
}

const emptyForm = { title: "", category: "general", content: "" };

export default function Discussions() {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: "", message: "" });
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("category", category);
    if (search.trim()) params.set("search", search.trim());
    return params.toString();
  }, [category, search]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`/api/discussions?${queryParams}`)
      .then(({ data }) => {
        if (!mounted || !data.success) return;
        setItems(data.discussions || []);
      })
      .catch(() => {
        if (mounted) setNotice({ type: "error", message: "Discussions could not be loaded." });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [queryParams]);

  const submitDiscussion = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setNotice({ type: "", message: "" });

    try {
      const { data } = await axios.post("/api/discussions", form, { withCredentials: true });
      if (data.success && data.discussion) {
        setForm(emptyForm);
        navigate(`/discussions/${data.discussion.id}`);
      }
    } catch (error) {
      setNotice({
        type: "error",
        message: error?.response?.data?.message || "Discussion could not be created.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Banner
        backgroundImage={BannerImg}
        heading="Discussions"
        paragraph="Lightweave Beacon Legacy is a space for artists to connect, share, grow, and support each other. Every member belongs here."
        minHeight="100vh"
        padding="80px 0"
        bton="Members please sign-in"
      />
      <Breadcrumb items={[{ label: "Discussions" }]} />
      <main className="bg-[#F9FAFB] px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <div>
              <p className="font-outfit text-xs font-bold uppercase tracking-[0.22em] text-cyan-700">
                Community discussions
              </p>
              <h1 className="mt-3 font-cinzel text-4xl text-gray-950 md:text-5xl">
                Join a Discussion
              </h1>
              <p className="mt-4 max-w-2xl font-outfit text-sm leading-7 text-gray-600">
                Ask a technique question, introduce yourself, share inspiration, or start a small peer conversation with other LWBL members.
              </p>
            </div>
            <div className="rounded-2xl border border-cyan-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700">
                  <Users size={20} />
                </div>
                <div>
                  <p className="font-cinzel text-lg text-gray-950">Member-led</p>
                  <p className="font-outfit text-sm text-gray-500">Constructive posts and replies from the community.</p>
                </div>
              </div>
            </div>
          </div>

          {notice.message && (
            <div className={`mb-5 rounded-2xl border px-4 py-3 font-outfit text-sm ${
              notice.type === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}>
              {notice.message}
            </div>
          )}

          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            <section>
              <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm md:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search discussions"
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 font-outfit text-sm outline-none focus:border-cyan-400"
                  />
                </div>
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  className="rounded-xl border border-gray-200 px-4 py-3 font-outfit text-sm outline-none focus:border-cyan-400"
                >
                  {categories.map((item) => (
                    <option key={item.key} value={item.key}>{item.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center font-outfit text-sm text-gray-500">
                    Loading discussions...
                  </div>
                ) : items.length ? (
                  items.map((item) => (
                    <Link
                      key={item.id}
                      to={`/discussions/${item.id}`}
                      className="block rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-200 hover:shadow-md"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <span className="rounded-full bg-cyan-50 px-3 py-1 font-outfit text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
                            {item.category}
                          </span>
                          <h2 className="mt-3 font-cinzel text-2xl text-gray-950">{item.title}</h2>
                          <p className="mt-2 line-clamp-2 font-outfit text-sm leading-6 text-gray-600">{item.content}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 font-outfit text-sm text-gray-600">
                          <MessageCircle size={16} />
                          {item.replyCount || 0}
                        </div>
                      </div>
                      <p className="mt-4 font-outfit text-xs text-gray-500">
                        {authorName(item.author)} - {timeAgo(item.lastActivityAt || item.createdAt)}
                      </p>
                    </Link>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                    <p className="font-cinzel text-xl text-gray-950">No discussions yet</p>
                    <p className="mt-2 font-outfit text-sm text-gray-500">Start the first conversation for this category.</p>
                  </div>
                )}
              </div>
            </section>

            <aside className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm lg:sticky lg:top-6 lg:self-start">
              <div className="mb-4 flex items-center gap-2">
                <Plus size={18} className="text-cyan-700" />
                <h2 className="font-cinzel text-xl text-gray-950">Start a Discussion</h2>
              </div>
              <form onSubmit={submitDiscussion} className="space-y-4">
                <input
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Discussion title"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-outfit text-sm outline-none focus:border-cyan-400"
                  maxLength={140}
                  required
                />
                <select
                  value={form.category}
                  onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-outfit text-sm outline-none focus:border-cyan-400"
                >
                  {categories.filter((item) => item.key !== "all").map((item) => (
                    <option key={item.key} value={item.key}>{item.label}</option>
                  ))}
                </select>
                <textarea
                  value={form.content}
                  onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
                  placeholder="Write the question, introduction, or idea you want members to respond to."
                  rows={7}
                  className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 font-outfit text-sm outline-none focus:border-cyan-400"
                  maxLength={3000}
                  required
                />
                <button
                  type="submit"
                  disabled={submitting || !userData}
                  className="w-full rounded-full bg-gray-900 px-5 py-3 font-outfit text-sm font-semibold text-white transition hover:bg-cyan-600 disabled:opacity-60"
                >
                  {submitting ? "Posting..." : "Post Discussion"}
                </button>
              </form>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
